import * as React from 'react';
import { DecoratorNode } from 'lexical';
import { Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import ImageViewer from '../components/ImageViewer';

const InlineImage = styled('img')({
    maxWidth: '100%',
    verticalAlign: 'middle',
});

const InlineImageButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    maxWidth: '100%',
    padding: 0,
    margin: 0,
    border: 'none',
    background: 'none',
    lineHeight: 0,
    cursor: 'zoom-in',
    verticalAlign: 'middle',
    '&:focus-visible': {
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: 2,
    },
}));

const InlineImageFallback = styled('span')(({ theme }) => ({
    display: 'inline-block',
    padding: theme.spacing(0.5, 1),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.secondary,
    verticalAlign: 'middle',
}));

const InlineImageView = ({ editor, src }) => {
    const [editable, setEditable] = React.useState(() => editor.isEditable());
    const [viewerOpen, setViewerOpen] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    const viewerAttachment = React.useMemo(() => ({ name: 'image', url: src }), [src]);

    React.useEffect(() => editor.registerEditableListener(setEditable), [editor]);
    React.useEffect(() => setFailed(false), [src]);

    if (failed) return <InlineImageFallback>Image could not be loaded</InlineImageFallback>;

    if (editable) return <InlineImage src={src} alt="" onError={() => setFailed(true)} />;

    return (
        <>
            <Tooltip title="Click to view">
                <InlineImageButton type="button" aria-label="View image" onClick={() => setViewerOpen(true)}>
                    <InlineImage src={src} alt="" onError={() => setFailed(true)} />
                </InlineImageButton>
            </Tooltip>
            {viewerOpen && <ImageViewer open={viewerOpen} onClose={() => setViewerOpen(false)} attachment={viewerAttachment} />}
        </>
    );
};

export class ImageNode extends DecoratorNode {
    __src;

    static getType() {
        return 'image';
    }

    static clone(node) {
        return new ImageNode(node.__src, node.__key);
    }

    constructor(src, key) {
        super(key);
        this.__src = src;
    }

    createDOM() {
        const span = document.createElement('span');
        span.style.display = 'inline-block';
        return span;
    }

    updateDOM() {
        return false;
    }

    isInline() {
        return true;
    }

    static importDOM() {
        return {
            img: () => ({
                conversion: (domNode) => {
                    if (domNode instanceof HTMLImageElement) {
                        const src = domNode.getAttribute('src') || domNode.src;
                        if (src) return { node: $createImageNode(src) };
                    }
                    return null;
                },
                priority: 0,
            }),
        };
    }

    static importJSON(serializedNode) {
        return $createImageNode(serializedNode.src);
    }

    exportJSON() {
        return {
            src: this.__src,
            type: 'image',
            version: 1,
        };
    }

    decorate(editor) {
        return <InlineImageView editor={editor} src={this.__src} />;
    }
}

export function $createImageNode(src) {
    return new ImageNode(src);
}

export function $isImageNode(node) {
    return node instanceof ImageNode;
}
